import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import "../styleSheets/modal.css"

type ModalProps = {
    isOpen: boolean;
    onClose?: () => void;
    children: React.ReactNode;
    id?: string;
    ariaLabel?: string;
};

const modalRoot = typeof document !== 'undefined' ? document.body : null;

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function Modal({ isOpen, onClose, children, id, ariaLabel }: ModalProps) {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const previousActiveElementRef = useRef<HTMLElement | null>(null);

    const focusFirstElement = () => {
        const content = contentRef.current;
        if (!content) return;

        const focusableElements = Array.from(
            content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((element) => element.tabIndex !== -1 && !element.hasAttribute("hidden"));

        const target = focusableElements.find((element) =>
            element.getAttribute("aria-hidden") !== "true",
        );

        (target ?? content).focus();
    };

    // Lock scroll while modal is open
    useEffect(() => {
        if (!isOpen) return;
        previousActiveElementRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;

        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const frameId = window.requestAnimationFrame(() => {
            focusFirstElement();
        });

        return () => {
            window.cancelAnimationFrame(frameId);
            document.body.style.overflow = prev;

            const previous = previousActiveElementRef.current;
            previousActiveElementRef.current = null;

            if (previous && document.contains(previous)) {
                previous.focus();
            }
        };
    }, [isOpen]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape" && onClose) {
            event.preventDefault();
            onClose();
            return;
        }

        if (event.key !== "Tab") return;

        const content = contentRef.current;
        if (!content) return;

        const focusableElements = Array.from(
            content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((element) => element.tabIndex !== -1 && !element.hasAttribute("hidden"));

        if (focusableElements.length === 0) {
            event.preventDefault();
            content.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        if (event.shiftKey) {
            if (activeElement === firstElement || activeElement === content) {
                event.preventDefault();
                lastElement.focus();
            }
        } else if (activeElement === lastElement || activeElement === content) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    if (!isOpen || !modalRoot) return null;

    return ReactDOM.createPortal(
        <div className="mtam-modal-overlay" onClick={onClose}>
            <div
                className="mtam-modal-content"
                id={id}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                ref={contentRef}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        modalRoot
    );
}
